<template>
  <div class="ticket-portal-detail">
    <loading-placeholder v-if="isLoading" :lines="10" />
    <template v-else>
      <ticket-portal-header 
        :ticket-portal-name="detail.ticketPortalName"
        :customer="detail.customer"
        :logo-url="detail.logoUrl"
      />
      <div class="thread">
        <div class="subject-and-status">
          <div>
            <span v-if="detail.refNumber" :class="{'with-status':detail.status}">#{{detail.refNumber}}</span>
            <status-chicklet v-if="detail.status" :status="detail.status" size="medium" />
          </div>
          <div>
            {{ detail.subject }}
          </div>
        </div>
        <div
          v-for="(threadEvent, index) in detail.listThreadEvents"
          :key="index"
          :class="{'reply-from-outside-user' : threadEvent.isOutsideUserInitiated }"
          class="post"
        >
          <div class="author-and-date">
            {{ threadEvent.creator.name }}, {{ threadEvent.createdOn }}
          </div>
          <div class="user-inputed-rich-text" v-html="threadEvent.description" />
          <div v-if="hasOtherInfo(threadEvent)" class="other-info">
            <!-- ATTACHMENTS LIST -->
            <attachments-list :attachments="threadEvent.listAttachments" />
            <!-- EMAIL DELIVERY NOTES -->
            <email-delivery-notes
              :ccs="threadEvent.ccs"
              :auto-response-sent="threadEvent.autoResponseSent"
              :bounce-info="threadEvent.bounceInfo"
            />
          </div>
        </div>
        <div class="editor-wrapper">
          <editor
            ref="editor"
            v-model="message"
            :ticket-portal-convo-id="ticketID"
            allows-attachment
            name="message"
            placeholder="Write your reply..."
            @files-list="addFileHandler"
            @file-list-removed="removeFileHandler"
          />
          <div class="editor-wrapper__footer">
            <el-tooltip v-if="isOfficeClosed">
              <template #content>
                <table>
                  <tbody>
                    <tr v-for="(info, index) in detail.listOpenHoursInfos" :key="index">
                      <td style="padding-right: 10px;">
                        {{info.dayOfWeek}}
                      </td>
                      <td>
                        <span v-if="info.isClosed">Closed</span>
                        <span v-else>{{info.startTime}}&ndash;{{info.endTime}}</span>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="2" style="padding-top:4px;">
                        <em>{{detail.officeHoursTimezone}}</em>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <span class="office-hours-message"><i class="ddi ddi-warning" />&nbsp;Currently outside working hours</span> 
            </el-tooltip>
            <dd-button
              :is-loading="isSubmitting"
              size="medium"
              color="dark-teal"
              @click="addReply"
            >Reply</dd-button>
          </div>
        </div>
      </div>
      <ticket-portal-footer />
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import TicketPortalHeader from './components/ticket-portal-header.vue'
import TicketPortalFooter from './components/ticket-portal-footer.vue'

/* Helpers */

/* Data types */
import { IAttachment } from '@/types/data-objects/attachments'

/* API */
import TicketPortalAPI from '@/api/modules/ticket-portal.api'
import {
  ITicketPortalOutputDetail,
  ITicketPortalOutputDetailThreadEvent,
} from '@/api/types/response.types'
import { ITicketPortalInputReplyFromCustomer } from '@/api/types/request.types'

export default defineComponent({
  name: 'TicketPortalListing',

  components: { TicketPortalHeader, TicketPortalFooter },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      detail: {} as ITicketPortalOutputDetail,
      message: '',
      fileUploadIDs: [] as number[],
    }
  },

  head(): any {
    return {
      title: this.detail.ticketPortalName,
    }
  },

  computed: {
    ticketID(): number {
      return parseInt(this.$route.params.ticketId, 10)
    },
    isOfficeClosed(): boolean {
      return (
        this.detail.listOpenHoursInfos !== null &&
        this.detail.isOutsideOfficeHours
      )
    },
  },

  async created() {
    this.isLoading = true
    await this.getTicketDetail()
    this.isLoading = false
  },

  methods: {
    addFileHandler(files: IAttachment[]) {
      this.fileUploadIDs = files.map(item => item.id)
    },
    async removeFileHandler(id: number) {
      try {
        await TicketPortalAPI.delete_FileUploadDelete(this.ticketID, id)
        this.fileUploadIDs = this.fileUploadIDs.filter(f => f !== id)
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },
    async getTicketDetail() {
      try {
        this.detail = await TicketPortalAPI.get_Detail(this.ticketID)
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async addReply() {
      try {
        this.isSubmitting = true
        const data = {
          message: this.message,
          listFileUploadIDs: this.fileUploadIDs,
        } as ITicketPortalInputReplyFromCustomer
        const response = await TicketPortalAPI.post_ReplyFromCustomer(
          this.ticketID,
          data,
        )
        this.$messaging.showSuccess(response.message)
        this.clearMessage()
        await this.getTicketDetail()
        this.isSubmitting = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
    clearMessage() {
      this.message = ''
    },
    hasOtherInfo(a: ITicketPortalOutputDetailThreadEvent): boolean {
      return (
        (a.listAttachments !== null && a.listAttachments.length > 0) ||
        (a.ccs !== null && a.ccs.length > 0) ||
        a.bounceInfo !== null ||
        a.autoResponseSent
      )
    },
  },
})
</script>

<style lang="scss">
.ticket-portal-detail {
  $side-margin-px: 30px;

  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--color-background-primary);
  padding-bottom: 100px;

  .loading-placeholder {
    margin-top: 20px;
    width: 90%;
  }

  .thread {
    margin: 30px 0;
    width: 800px;
    display: flex;
    flex-direction: column;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }

    .subject-and-status {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
      width: 100%;
      margin: 0 auto 30px auto;
      padding: 0 $side-margin-px;

      > div:first-child {
        @include lettering--h4;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;

        > .with-status {
          padding-right: 10px;
          border-right: solid 1px $color--gray-30;
        }
      }

      > div:last-child {
        @include lettering--h3;
      }

      @include media-breakpoint-down(sm) {
        flex-direction: column;
        text-align: center;
        align-items: center;
        gap: 20px;

        .status-chicklet {
          max-width: 50%;
        }
      }
    }

    .post {
      background-color: var(--color-background-header);
      border: solid 1px $color--gray-30;
      border-radius: 8px;
      margin-bottom: 10px;
      padding: $side-margin-px;
      display: flex;
      flex-direction: column;
      position: relative;

      @include media-breakpoint-up(md) {
        @include standard-box-shadow;
      }

      @include media-breakpoint-down(sm) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        border-radius: 0px;
        border-left: none;
        border-right: none;
      }

      .author-and-date {
        margin-top: -4px;
        padding-bottom: 6px;
        border-bottom: solid 1px $color--gray-45;
        margin-bottom: 20px;
        @include lettering--p-sm;
        font-weight: $bold;
      }

      .other-info {
        margin-top: 24px;
      }

      &.reply-from-outside-user {
        background-color: var(--color-background-primary);
      }
    }

    .editor-wrapper {
      background-color: var(--color-background-header);
      border: solid 1px $color--gray-30;
      border-radius: 8px;
      padding: $side-margin-px;

      &__footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        margin-top: 10px;
        gap: 10px;

        .office-hours-message {
          @include lettering--p-sm--no-color;
          color: var(--color-text-primary);
          cursor: default;
        }
      }

      @include media-breakpoint-up(md) {
        @include standard-box-shadow;
      }

      @include media-breakpoint-down(sm) {
        border-radius: 0px;
        border-left: none;
        border-right: none;
      }

      > .dd-button {
        margin: 10px 0 0 auto;
      }
    }
  }
}
</style>
